import { getStep1FormConfig } from '@/screens/ReportCreation/forms/step1.config';
import { getStep2DetailFormConfig } from '@/screens/ReportCreation/forms/step2.config';
import { getStep3DetailFormConfig } from '@/screens/ReportCreation/forms/step3.config';
import type {
  FormField,
  FormLayout,
  FormOption,
} from '@/screens/ReportCreation/reportCreation.types';
import { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';
import {
  CsvTemplateColumn,
  CsvTemplateHintLabels,
  GetHeadersAndHintsResponse,
} from '@screens/ReportCreation/components/CsvImport/types/csvTemplates.types';
import { CSV_DELIMITER } from '@screens/ReportCreation/components/CsvImport/constants/csvParser.constants';
import {
  DIAGNOSTIC_PROCEDURES_TEMPLATE_LIMIT,
  getDiagnosticBasePath,
} from '@screens/ReportCreation/components/CsvImport/constants/csvTemplate.constants';

const escapeSemicolonCsvValue = (
  value: string | number | null | undefined,
): string => {
  const stringValue =
    value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const buildSemicolonCsvContent = (
  headers: string[],
  rows: (string | number | null | undefined)[][],
): string =>
  [headers, ...rows]
    .map((row) => row.map(escapeSemicolonCsvValue).join(CSV_DELIMITER))
    .join('\r\n');

const joinOptionLabels = (options: FormOption[]): string =>
  options.map((option) => option.label).join(' | ');

const buildHintLabels = (
  t: TFunction,
  language: SupportedLanguage,
): CsvTemplateHintLabels => ({
  dynamicOptionExactMatch: t(
    'reportCreation.csvTemplate.hints.dynamicOptionExactMatch',
    { lng: language },
  ),
  species: t('reportCreation.csvTemplate.hints.species', { lng: language }),
  yesNo: t('reportCreation.csvTemplate.hints.yesNo', { lng: language }),
  date: t('reportCreation.csvTemplate.hints.date', { lng: language }),
  populationSize: t('reportCreation.csvTemplate.hints.populationSize', {
    lng: language,
  }),
  totalCount: t('reportCreation.csvTemplate.hints.totalCount', {
    lng: language,
  }),
  nonNegativeNumber: t('reportCreation.csvTemplate.hints.nonNegativeNumber', {
    lng: language,
  }),
  freeText: t('reportCreation.csvTemplate.hints.freeText', { lng: language }),
  latitude: t('reportCreation.csvTemplate.hints.latitude', { lng: language }),
  longitude: t('reportCreation.csvTemplate.hints.longitude', { lng: language }),
});

const buildHint = (
  field: FormField,
  t: TFunction,
  hintLabels: CsvTemplateHintLabels,
  language: SupportedLanguage,
): string => {
  switch (field.type) {
    case 'toggle-group':
      if (field.options.length > 0) {
        return joinOptionLabels(field.options);
      }
      return '';
    case 'select':
      if (field.options.length > 0) {
        return joinOptionLabels(field.options);
      }
      if (field.dynamicOptions) {
        return hintLabels.dynamicOptionExactMatch;
      }
      return '';
    case 'autocomplete-search':
      if (field.options.length > 0) {
        return joinOptionLabels(field.options);
      }
      return hintLabels.dynamicOptionExactMatch;
    case 'species-search':
      return hintLabels.species;
    case 'checkbox':
      return hintLabels.yesNo;
    case 'date':
      return hintLabels.date;
    case 'numeric':
      if (field.id === 'populationSize') {
        return hintLabels.populationSize;
      }
      if (field.id.endsWith('.totalCount')) {
        return hintLabels.totalCount;
      }
      return hintLabels.nonNegativeNumber;
    case 'textarea':
      if (typeof field.maxLength === 'number') {
        return t('reportCreation.csvTemplate.hints.maxCharacters', {
          count: field.maxLength,
          lng: language,
        });
      }
      return hintLabels.freeText;
    default:
      return '';
  }
};

const buildLocationColumns = (
  t: TFunction,
  hintLabels: CsvTemplateHintLabels,
  language: SupportedLanguage,
  formatHeader: (label: string) => string = (label) => label,
): CsvTemplateColumn[] => [
  {
    key: 'location.latitude',
    header: formatHeader(t('pinPosition.latitude', { lng: language })),
    hint: hintLabels.latitude,
  },
  {
    key: 'location.longitude',
    header: formatHeader(t('pinPosition.longitude', { lng: language })),
    hint: hintLabels.longitude,
  },
];

const buildColumnsFromFormLayout = (
  layout: FormLayout,
  t: TFunction,
  hintLabels: CsvTemplateHintLabels,
  language: SupportedLanguage,
): CsvTemplateColumn[] => {
  return (
    layout.sections?.flatMap((section) =>
      section.fields.flatMap((field) => {
        if (field.type === 'location') {
          return buildLocationColumns(t, hintLabels, language);
        }

        return [
          {
            key: field.id,
            header: field.label ?? section.title ?? String(field.id),
            hint: buildHint(field, t, hintLabels, language),
          },
        ];
      }),
    ) ?? []
  );
};

const buildDiagnosticProcedureColumns = (
  layout: FormLayout,
  t: TFunction,
  hintLabels: CsvTemplateHintLabels,
  language: SupportedLanguage,
): CsvTemplateColumn[] => {
  const repeatableSection = layout.repeatableSections?.[0];
  if (!repeatableSection) {
    return [];
  }

  return Array.from({ length: DIAGNOSTIC_PROCEDURES_TEMPLATE_LIMIT }).flatMap(
    (_, index) => {
      const basePath = getDiagnosticBasePath(index);
      return repeatableSection.sections.flatMap((section) =>
        section.fields.flatMap((field) => {
          const fieldHeader = field.label ?? section.title ?? String(field.id);
          return [
            {
              key: `${basePath}.${field.id}`,
              header: formatDiagnosticColumnHeader(
                t,
                language,
                index + 1,
                fieldHeader,
              ),
              hint: buildHint(field, t, hintLabels, language),
            },
          ];
        }),
      );
    },
  );
};

export const formatDiagnosticColumnHeader = (
  t: TFunction,
  language: SupportedLanguage,
  index: number,
  label: string,
): string =>
  `${t('reportCreation.csvTemplate.diagnosticPrefix', { lng: language })} #${index} - ${label}`;

export const getCsvColumns = (
  t: TFunction,
  language: SupportedLanguage,
): CsvTemplateColumn[] => {
  const hintLabels = buildHintLabels(t, language);
  const step1 = getStep1FormConfig(t, language);
  const step2 = getStep2DetailFormConfig(t, language);
  const step3 = getStep3DetailFormConfig(t, language);

  return [
    ...buildColumnsFromFormLayout(step1, t, hintLabels, language),
    ...buildColumnsFromFormLayout(step2, t, hintLabels, language),
    ...buildDiagnosticProcedureColumns(step3, t, hintLabels, language),
  ];
};

export const getHeadersAndHints = (
  t: TFunction,
  language: SupportedLanguage,
): GetHeadersAndHintsResponse => {
  const columns = getCsvColumns(t, language);

  return {
    headers: columns.map((column) => column.header),
    hints: columns.map((column) => column.hint),
  };
};

export const buildReportCreationTemplateCsvContent = (
  t: TFunction,
  language: SupportedLanguage,
): string => {
  const { headers, hints } = getHeadersAndHints(t, language);

  return buildSemicolonCsvContent(headers, [hints]);
};
